import React, { ReactElement, useEffect, useState } from "react";
import { ZSelect } from "@atoms";
import { IOptionProps } from "@atoms/ZSelect/interface";
import { IDynamicSelectProps } from "./interface";

import { Empty, Skeleton } from "antd";
import useComponentDidUpdate from "@/Hooks/useComponentDidUpdate";
import useDebounce from "use-debounce/lib/useDebounce";
import { SelectValue } from "antd/lib/select";
import { filterOptionsOnSearch } from "@/Contracts";

const ZDynamicSelect = <T extends SelectValue = string>(
	props: IDynamicSelectProps<T>
): ReactElement => {
	const [filteredOptions, setFilteredOptions] = useState<IOptionProps[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [searchKeyword, setSearchKeyword] = useState<string>("");
	const {
		defaultOptions,
		options,
		placeholder,
		dropdownRender,
		onChange,
		fetchData,
		shouldFetchDefaultData,
		isMultiSelect,
		onSearch,
		...rest
	} = props;

	const [deBouncedSearchedTerm] = useDebounce(searchKeyword, 500);

	const handleSearch = (value: string) => {
		if (value.length >= 3) {
			fetchDynamically(value);
		} else if (shouldFetchDefaultData) {
			fetchDynamically(value);
		} else {
			setFilteredOptions(defaultOptions ?? []);
		}
	};

	const updateState = (value: string) => {
		setSearchKeyword(value);
		onSearch?.(value);
	};

	const fetchDynamically = async (value: string) => {
		try {
			setLoading(true);
			await fetchData(value);
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setFilteredOptions(defaultOptions ?? []);
	}, [defaultOptions]);

	useEffect(() => {
		if (searchKeyword.length || props.shouldFetchDefaultData) {
			setFilteredOptions(options ?? []);
		}
	}, [options]);

	useComponentDidUpdate(() => {
		handleSearch(deBouncedSearchedTerm);
	}, [deBouncedSearchedTerm]);

	const mode = isMultiSelect ? "multiple" : undefined;

	return (
		<ZSelect<T>
			className={props.className}
			showSearch
			allowClear
			loading={loading}
			onSearch={updateState}
			selectOptions={filteredOptions}
			placeholder={placeholder}
			filterOption={filterOptionsOnSearch}
			dropdownRender={dropdownRender}
			onChange={onChange}
			mode={mode}
			getPopupContainer={trigger => trigger.parentNode}
			useThreeLetterSearch
			notFoundContent={
				searchKeyword && searchKeyword.length >= 3 ? (
					loading ? (
						<Skeleton
							title={false}
							active
							paragraph={{ rows: 1 }}
						/>
					) : (
						<Empty />
					)
				) : (
					<div className="search-information">
						Please type atleast 3 letters to start searching
					</div>
				)
			}
			{...rest}
		/>
	);
};

export default ZDynamicSelect;
